'use client';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { useAppState } from '@/lib/useAppState';
import { getUserColor, getInitials } from '@/lib/userColor';
import clsx from 'clsx';
import { Lightbulb } from 'lucide-react';
import { ASSISTANT_DISPLAY_NAME, isAssistantAuthor } from '@/lib/discussionAssistant';

const SIZES = {
	xs: { avatar: 'h-4 w-4', text: 'text-[8px]' },
	sm: { avatar: 'h-6 w-6', text: 'text-[10px]' },
	md: { avatar: 'h-7 w-7', text: 'text-[10px]' },
	lg: { avatar: 'h-8 w-8', text: 'text-xs' },
} as const;

export type SystemUserAvatarSize = keyof typeof SIZES;

interface SystemUserAvatarProps {
	/** Supabase auth.users UUID — used to look up email from workspaceMembers and derive color */
	systemUserId: string;
	size?: SystemUserAvatarSize;
	className?: string;
	/** Show email tooltip on hover */
	showTooltip?: boolean;
	/** Extra border for stacked avatar layouts */
	border?: boolean;
	/** Optional image URL (e.g. Google profile picture). Falls back to initials if missing or fails to load. */
	avatarUrl?: string;
	/** Optional override for fallback alt text / tooltip (e.g. Google-provided name). */
	displayName?: string;
}

// Looks up the user's email from workspaceMembers in Zustand state, and derives a
// deterministic color and initials from it.
export function SystemUserAvatar({
	systemUserId,
	size = 'md',
	className,
	showTooltip = false,
	border = false,
	avatarUrl,
	displayName,
}: SystemUserAvatarProps) {
	const workspaceMembers = useAppState((s) => s.workspaceMembers);

	// Callers such as the takeover banner can hold a user whose admin id was never recorded.
	if (!systemUserId) return null;
	if (isAssistantAuthor(systemUserId)) {
		return <AssistantAvatar size={size} className={className} showTooltip={showTooltip} />;
	}

	const member = workspaceMembers.find(m => m.systemUserId === systemUserId);
	const email = member?.email ?? systemUserId.slice(0, 8);
	// Fall back to the workspace member's name/avatar when the caller doesn't
	// pass one explicitly — so any existing avatar in the app picks up Google
	// profile data automatically.
	const resolvedAvatarUrl = avatarUrl ?? member?.avatarUrl;
	const resolvedDisplayName = displayName ?? member?.name;
	const color = getUserColor(systemUserId);
	const initials = getInitials(resolvedDisplayName || email);
	const tooltipLabel = resolvedDisplayName || email;
	const s = SIZES[size];

	const avatar = (
		<Avatar className={clsx(
			s.avatar,
			'flex-shrink-0 bg-card dark:bg-card ring-1 ring-border dark:ring-border',
			border && 'border-1 border-white dark:border-border',
			className,
		)}>
			{resolvedAvatarUrl && (
				<AvatarImage
					src={resolvedAvatarUrl}
					alt={tooltipLabel}
					referrerPolicy="no-referrer"
				/>
			)}
			<AvatarFallback
				className={clsx(s.text, 'font-medium text-white')}
				style={{ backgroundColor: color }}
			>
				{initials}
			</AvatarFallback>
		</Avatar>
	);

	if (!showTooltip) return avatar;

	return (
		<Tooltip>
			<TooltipTrigger asChild>{avatar}</TooltipTrigger>
			<TooltipContent side="bottom" className="text-xs">{tooltipLabel}</TooltipContent>
		</Tooltip>
	);
}

function AssistantAvatar({
	size,
	className,
	showTooltip,
}: {
	size: SystemUserAvatarSize;
	className?: string;
	showTooltip: boolean;
}) {
	const s = SIZES[size];
	const avatar = (
		<Avatar className={clsx(s.avatar, 'flex-shrink-0 ring-1 ring-border', className)}>
			<AvatarFallback className="bg-primary text-primary-foreground" aria-label={ASSISTANT_DISPLAY_NAME}>
				<Lightbulb className="h-1/2 w-1/2" />
			</AvatarFallback>
		</Avatar>
	);

	if (!showTooltip) return avatar;

	return (
		<Tooltip>
			<TooltipTrigger asChild>{avatar}</TooltipTrigger>
			<TooltipContent side="bottom" className="text-xs">{ASSISTANT_DISPLAY_NAME}</TooltipContent>
		</Tooltip>
	);
}
